import { Trans } from "@lingui/react/macro";
import { Box, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import type { Locale } from "date-fns";
import {
  addDays,
  differenceInCalendarDays,
  eachDayOfInterval,
  format,
  isSameDay,
  isSameMonth,
} from "date-fns";

const COL_W = 44; // px per day column
const PILL_INSET = 3; // px gap inside column span on each side
const LANE_H = 40; // px per lane row

interface AlternativeDatesTimelineProps {
  validArrivalDays: Date[];
  selectedNights: number;
  selectedFrom: Date | undefined;
  onArrivalSelect: (day: Date) => void;
  locale: Locale;
  unavailableFrom?: Date;
  unavailableUntil?: Date;
}

export default function AlternativeDatesTimeline({
  validArrivalDays,
  selectedNights,
  selectedFrom,
  onArrivalSelect,
  locale,
  unavailableFrom,
  unavailableUntil,
}: AlternativeDatesTimelineProps) {
  const theme = useTheme();

  if (validArrivalDays.length === 0) {
    return (
      <Box sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
        <Typography variant="body2" color="text.secondary">
          <Trans>No available dates for this period length.</Trans>
        </Typography>
      </Box>
    );
  }

  const sortedArrivals = [...validArrivalDays].sort((a, b) => a.getTime() - b.getTime());

  let rangeStart = sortedArrivals[0]!;
  let rangeEnd = addDays(sortedArrivals[sortedArrivals.length - 1]!, selectedNights);
  if (unavailableFrom && unavailableUntil) {
    if (unavailableFrom.getTime() < rangeStart.getTime()) rangeStart = unavailableFrom;
    if (unavailableUntil.getTime() > rangeEnd.getTime()) rangeEnd = unavailableUntil;
  }
  const allDays = eachDayOfInterval({ start: rangeStart, end: rangeEnd });
  const totalWidth = allDays.length * COL_W;

  // Greedy lane packing: assign each period to the first lane it fits in
  const lanes: Date[][] = [];
  for (const arrival of sortedArrivals) {
    let placed = false;
    for (const lane of lanes) {
      const lastDeparture = addDays(lane[lane.length - 1]!, selectedNights);
      if (lastDeparture < arrival) {
        lane.push(arrival);
        placed = true;
        break;
      }
    }
    if (!placed) lanes.push([arrival]);
  }

  return (
    <Box sx={{ overflowX: "auto" }}>
      <Box sx={{ width: totalWidth }}>
        {/* Month header row */}
        {(() => {
          const segments: { label: string; count: number }[] = [];
          for (const [i, day] of allDays.entries()) {
            const last = segments[segments.length - 1];
            if (last && i > 0 && isSameMonth(day, allDays[i - 1]!)) {
              last.count++;
            } else {
              segments.push({ label: format(day, "MMM yyyy", { locale }), count: 1 });
            }
          }
          return (
            <Box sx={{ display: "flex", borderBottom: "1px solid", borderColor: "divider" }}>
              {segments.map((seg) => (
                <Box
                  key={seg.label}
                  sx={{
                    width: seg.count * COL_W,
                    flexShrink: 0,
                    pl: 0.75,
                    py: 0.25,
                    borderRight: "1px solid",
                    borderColor: "divider",
                    boxSizing: "border-box",
                  }}
                >
                  <Typography variant="caption" color="primary" fontWeight={600}>
                    {seg.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          );
        })()}

        {/* Day header row */}
        <Box
          sx={{
            display: "flex",
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          {allDays.map((day) => {
            const isWeekend = day.getDay() === 0 || day.getDay() === 6;
            return (
              <Box
                key={day.toISOString()}
                sx={{
                  width: COL_W,
                  flexShrink: 0,
                  textAlign: "center",
                  py: 0.5,
                  borderRight: "1px solid",
                  borderColor: "divider",
                  boxSizing: "border-box",
                  bgcolor: isWeekend ? alpha(theme.palette.action.selected, 0.03) : undefined,
                }}
              >
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                  lineHeight={1.2}
                >
                  {format(day, "EEE", { locale })}
                </Typography>
                <Typography variant="caption" display="block" fontWeight={600} lineHeight={1}>
                  {format(day, "d", { locale })}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Unavailable (originally searched) period */}
        {unavailableFrom &&
          unavailableUntil &&
          (() => {
            const colIdx = differenceInCalendarDays(unavailableFrom, rangeStart);
            const departure = unavailableUntil;
            const unavailableNights = differenceInCalendarDays(departure, unavailableFrom);
            return (
              <Box
                sx={{
                  position: "relative",
                  height: LANE_H,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Box
                  sx={{ position: "absolute", inset: 0, display: "flex", pointerEvents: "none" }}
                >
                  {allDays.map((day) => {
                    const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                    return (
                      <Box
                        key={day.toISOString()}
                        sx={{
                          width: COL_W,
                          flexShrink: 0,
                          borderRight: "1px solid",
                          borderColor: "divider",
                          height: "100%",
                          bgcolor: isWeekend
                            ? alpha(theme.palette.action.selected, 0.03)
                            : undefined,
                        }}
                      />
                    );
                  })}
                </Box>
                <Box
                  title={`${format(unavailableFrom, "EEE d MMM", { locale })} – ${format(departure, "EEE d MMM", { locale })}`}
                  sx={{
                    position: "absolute",
                    left: colIdx * COL_W + PILL_INSET,
                    top: 4,
                    width: (unavailableNights + 1) * COL_W - PILL_INSET * 2,
                    height: LANE_H - 8,
                    borderRadius: 10,
                    bgcolor: alpha(theme.palette.error.main, 0.12),
                    color: "error.main",
                    border: "1.5px solid",
                    borderColor: alpha(theme.palette.error.main, 0.4),
                    display: "flex",
                    alignItems: "center",
                    px: 1,
                    overflow: "hidden",
                  }}
                >
                  <Typography variant="caption" noWrap>
                    <Trans>Unavailable</Trans>
                  </Typography>
                </Box>
              </Box>
            );
          })()}

        {/* Lanes */}
        {lanes.map((lane, idx) => (
          <Box
            key={lane[0]!.toISOString()}
            sx={{
              position: "relative",
              height: LANE_H,
              borderBottom: lanes.length > idx + 1 ? "1px solid" : undefined,
              borderColor: "divider",
            }}
          >
            {/* Column grid lines */}
            <Box sx={{ position: "absolute", inset: 0, display: "flex", pointerEvents: "none" }}>
              {allDays.map((day) => {
                const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                return (
                  <Box
                    key={day.toISOString()}
                    sx={{
                      width: COL_W,
                      flexShrink: 0,
                      borderRight: "1px solid",
                      borderColor: "divider",
                      height: "100%",
                      bgcolor: isWeekend ? alpha(theme.palette.action.selected, 0.03) : undefined,
                    }}
                  />
                );
              })}
            </Box>

            {/* Period pills */}
            {lane.map((arrival) => {
              const colIdx = differenceInCalendarDays(arrival, rangeStart);
              const isSelected = !!selectedFrom && isSameDay(arrival, selectedFrom);
              const departure = addDays(arrival, selectedNights);

              return (
                <Box
                  key={arrival.toISOString()}
                  onClick={() => onArrivalSelect(arrival)}
                  title={`${format(arrival, "EEE d MMM", { locale })} – ${format(departure, "EEE d MMM", { locale })}`}
                  sx={{
                    position: "absolute",
                    left: colIdx * COL_W + PILL_INSET,
                    top: 4,
                    width: (selectedNights + 1) * COL_W - PILL_INSET * 2,
                    height: LANE_H - 8,
                    borderRadius: 10,
                    bgcolor: isSelected ? "primary.main" : alpha(theme.palette.primary.main, 0.12),
                    color: isSelected ? "primary.contrastText" : "primary.main",
                    border: `1.5px solid`,
                    borderColor: isSelected
                      ? "primary.main"
                      : alpha(theme.palette.primary.main, 0.4),
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    px: 1,
                    overflow: "hidden",
                    "&:hover": {
                      bgcolor: isSelected
                        ? "primary.dark"
                        : alpha(theme.palette.primary.main, 0.24),
                    },
                  }}
                >
                  {selectedNights > 1 ? (
                    <Typography variant="caption" noWrap>
                      {format(arrival, "d MMM", { locale })}–
                      {format(departure, "d MMM", { locale })}
                    </Typography>
                  ) : (
                    <Typography variant="caption" noWrap>
                      {format(arrival, "d.MM", { locale })}–{format(departure, "d.MM", { locale })}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
